import { TableColumn } from '../types/commonTypes';
import { randomNumber } from './constants';
import { sanitizeForCsv } from './sanitize';

export const downloadCsv = (
  columns: TableColumn[],
  data: any[],
  title: string,
  includeHeader: boolean
) => {
  const csv = generateCsv(columns, data, includeHeader);

  createDownload(csv, title);
};

export const downloadCsvTemplate = (
  name: string,
  columns: CsvImportColumn[]
) => {
  const template = columns.reduce(
    (obj: { columns: any[]; data: object }, col) => {
      obj.columns.push({
        name: col.name,
        dataKey: col.name,
      });

      switch (col.type) {
        case 'boolean':
          obj.data = Object.assign(obj.data, { [col.name]: true });
          break;
        case 'number':
          obj.data = Object.assign(obj.data, { [col.name]: randomNumber() });
          break;
        case 'string':
        default:
          obj.data = Object.assign(obj.data, {
            [col.name]:
              col.options !== undefined ? col.options.join('|') : 'N/A',
          });
          break;
      }
      return obj;
    },
    { columns: [], data: {} }
  );
  downloadCsv(template.columns, [template.data], `${name}_template`, true);
};

const generateCsv = (
  columns: TableColumn[],
  data: any[],
  includeHeader: boolean
) => {
  const csvHead =
    columns
      .reduce((line, col) => {
        if (col.isDownloadable ?? true) line += col.name + ',';
        return line;
      }, '')
      .slice(0, -1) + '\n';
  const csvBody = data
    .map((row) => {
      return columns
        .reduce((rowValues: any[], col) => {
          if (col.isDownloadable ?? true) {
            const value = row[col.dataKey];

            if (typeof value === 'object') {
              const sanitized = sanitizeForCsv(
                row[col.dataKey]?.value ?? 'N/A'
              );

              rowValues.push(sanitized);
            } else if (value === 'string') {
              const sanitized = sanitizeForCsv(value);

              rowValues.push(sanitized);
            } else if (typeof value === 'string' && value.includes(',')) {
              const sanitized = sanitizeForCsv(`"${row[col.dataKey]}"`);

              rowValues.push(sanitized);
            } else rowValues.push(sanitizeForCsv(value));
          }
          return rowValues;
        }, [])
        .join(',');
    })
    .join('\n');

  return includeHeader ? csvHead + csvBody : csvBody;
};

const createDownload = (csv: string, title: string) => {
  const encodedUri = encodeURI(
    'data:text/csv;charset=utf-8,' + csv.replace(/(#)/g, '')
  );
  let CSVLink = document.createElement('a');

  CSVLink.setAttribute('href', encodedUri);
  CSVLink.setAttribute('target', '_blank');
  CSVLink.setAttribute('download', `${title}.csv`);
  document.body.appendChild(CSVLink);
  CSVLink.click();
  CSVLink.parentNode?.removeChild(CSVLink);
};

//CSV Import Types and Methods
export type ColumnPrimitive = 'boolean' | 'number' | 'string';

export interface CsvImportColumn {
  charLength?: number;
  name: string;
  note?: string;
  nullable?: boolean;
  options?: string[];
  type: ColumnPrimitive;
  validator?(value: any, row: any): ValidationResult;
  formatter?(value: any): any;
}

export interface CsvImportResult<T> {
  data: Array<T>;
  error: string | null;
}

const convertToBoolean = (
  value: string,
  columnName: string,
  rowIndex: number
): boolean => {
  switch (value.toLowerCase().trim()) {
    case 'true':
    case '1':
      return true;
    case 'false':
    case '0':
      return false;
    default:
      throw `Line ${
        rowIndex + 2
      }, Column '${columnName}': Value must be a 'boolean'`;
  }
};

const convertToNumber = (
  value: string,
  columnName: string,
  rowIndex: number
): number => {
  const parsedValue = parseInt(value);
  if (!isNaN(parsedValue)) return parsedValue;
  throw `Line ${
    rowIndex + 2
  }, Column '${columnName}': Value must be a 'number'`;
};

const parseRowToType = <T>(
  value: string,
  column: CsvImportColumn,
  rowIndex: number,
  rowData: any
): any => {
  let parsedValue: any = value;
  const options = (column.options ?? []).map((o) => o.toLowerCase());

  if (
    Boolean(column.nullable) &&
    value.trim() !== '' &&
    options.length &&
    options.includes(value.toLowerCase().trim()) === false
  )
    throw `Line ${rowIndex + 2}, Column '${
      column.name
    }': Value must be one of ${(column.options ?? []).join(',')}`;

  if (
    !Boolean(column.nullable) &&
    column.charLength !== undefined &&
    value.trim().length !== column.charLength
  )
    throw `Line ${rowIndex + 2}, Column '${column.name}': Value must be ${
      column.charLength
    } characters long`;

  if (Boolean(column.nullable) && value.trim() === '') {
    parsedValue = null;
  } else if (column.type === 'boolean')
    parsedValue = convertToBoolean(value, column.name.toString(), rowIndex);
  else if (column.type === 'number')
    parsedValue = convertToNumber(value, column.name.toString(), rowIndex);
  else if (
    (column.type === 'string' && value.startsWith('"')) ||
    value.endsWith('"')
  )
    parsedValue = value.replaceAll('"', '');

  if (column.validator) {
    const result = column.validator(parsedValue, rowData);
    if (!result.isValid)
      throw `Line ${rowIndex + 2}, Column '${column.name}': ${
        result.message ?? 'Unknown Error'
      }`;
  }

  if (column.formatter) return column.formatter(parsedValue);
  return parsedValue;
};

const validateRequiredColumns = (
  columns: string[],
  requiredColumns: CsvImportColumn[]
) => {
  requiredColumns.forEach((rc) => {
    const exists =
      columns
        .map((column) => column.toLowerCase())
        .indexOf(rc.name.toLowerCase()) >= 0;
    if (exists === false) throw `Required column '${rc.name}' is missing...`;
  });
};

function parseCsvData<T>(
  data: string,
  requiredColumns?: CsvImportColumn[]
): Array<T> {
  const csvLines = data.replace(/\n?\r*$/, '').split(/\r?\n/);
  const columns = csvLines[0].split(',').map((column) => column.trim());
  const rows = csvLines.slice(1).reduce((array: any, row) => {
    const cells = row.split(',').reduce((arr: string[], row, idx) => {
      const cleaned = row.replaceAll('"', '').trim();
      if (idx > 0 && row.endsWith('"')) {
        const lastIdx = idx - 1;
        arr[lastIdx] = (arr[lastIdx] + ', ' + cleaned).trim();
      } else {
        arr.push(cleaned);
      }
      return arr;
    }, []);
    array.push(cells);
    return array;
  }, []);
  if (rows.some((row: string[]) => row.length !== columns.length))
    throw 'Parsing Error: One or more rows do not match CSV column count. If a cell value contains a comma, escape it with quotes.';

  if (requiredColumns !== undefined)
    validateRequiredColumns(columns, requiredColumns);

  return rows.reduce((arr: T[], row: string[], rowNumber: number) => {
    let obj = {};
    row.forEach((value, index) => {
      const requiredColumn = (requiredColumns ?? []).find(
        (c) => c.name.toLowerCase() === columns[index].toLowerCase()
      );
      if (requiredColumn !== undefined) {
        Object.assign(obj, {
          [columns[index]]: parseRowToType(
            value,
            requiredColumn,
            rowNumber,
            mapRowToObj(columns, rows[rowNumber])
          ),
        });
      } else Object.assign(obj, { [columns[index]]: value });
    });
    arr.push(obj as T);
    return arr;
  }, []);
}

const mapRowToObj = (columns: string[], rows: string[]) => {
  return columns.reduce((obj, column, index) => {
    obj = Object.assign({}, obj, { [column]: rows[index] });
    return obj;
  }, {});
};

async function readFile(file: File) {
  return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    let reader = new FileReader();

    reader.onloadend = () => {
      resolve(reader.result);
    };

    reader.onerror = () => reject('Unknown Error');

    reader.readAsText(file);
  });
}

export async function readCsv<T>(
  csv: File | null,
  callback: (csv: CsvImportResult<T>) => void,
  requiredColumns?: CsvImportColumn[]
) {
  if (csv === null) return { data: [], error: 'No File Found' };
  let result: CsvImportResult<T> = { data: [], error: 'Unknown Error' };

  readFile(csv)
    .then((value) => {
      if (typeof value !== 'string') return;
      result = {
        data: parseCsvData<T>(value.trim(), requiredColumns),
        error: null,
      };
    })
    .catch((reason) => {
      result = { data: [], error: reason };
    })
    .finally(() => callback(result));
}
